import { Routes } from '@angular/router';
import { MsalRedirectComponent } from '@azure/msal-angular';
import { DefaultLayoutComponent } from './core/layout/default-layout/default-layout.component';
import { AccessDeniedComponent } from './pages/access-denied/access-denied.component';
import { NotFoundComponent } from './pages/not-found/not-found.component';
import { DebugComponent } from './pages/debug/debug.component';
import { msalAuthGuard } from './core/guards/msal-auth.guard';
import { userLoadingGuard } from './core/guards/user-loading.guard';

/*
 * Route paths below mirror the legacy application's URL structure exactly
 * (see Frontend/src/app/app.routes.ts) so that bookmarked/shared legacy
 * URLs continue to resolve unchanged. Angular's Router is case-sensitive
 * by default, so casing must match legacy verbatim (e.g. `Personal-Dashboard`,
 * not `personal-dashboard`).
 */
export const routes: Routes = [
  // MSAL redirect callback handler (new infrastructure route, no legacy equivalent)
  { path: 'auth', component: MsalRedirectComponent },

  // Public pages (legacy: no AuthenticationGuard on these two routes)
  { path: 'Access-Denied', component: AccessDeniedComponent },
  { path: 'Debug', component: DebugComponent },

  // Protected layout — all children require MSAL auth + loaded user
  {
    path: '',
    component: DefaultLayoutComponent,
    canActivate: [msalAuthGuard, userLoadingGuard],
    canActivateChild: [msalAuthGuard],
    children: [
      // Landing route — legacy renders the dashboard directly at '/' (no redirect)
      {
        path: '',
        loadChildren: () =>
          import('./features/dashboard/dashboard.routes').then(m => m.DASHBOARD_ROUTES),
      },

      // Legacy backwards-compatibility redirect: /Reviews -> /Personal-Dashboard
      { path: 'Reviews', redirectTo: '/Personal-Dashboard', pathMatch: 'full' },

      // Feature 1: Dashboard (Phase 1)
      {
        path: 'Personal-Dashboard',
        loadChildren: () =>
          import('./features/dashboard/dashboard.routes').then(m => m.DASHBOARD_ROUTES),
      },

      // Feature 1b: Download Report (Phase 1)
      {
        path: 'Download-Report',
        loadChildren: () =>
          import('./features/dashboard/download-report.routes').then(m => m.DOWNLOAD_REPORT_ROUTES),
      },

      // Feature 2: Incidents — create, review, resolve (nested), claim (nested) (Phase 2/3/4/5)
      {
        path: 'Incidents',
        loadChildren: () =>
          import('./features/incidents/incidents.routes').then(m => m.INCIDENTS_ROUTES),
      },

      // Feature 3: SAP Quality Management (Phase 2)
      {
        path: 'SAP-Quality-Management',
        loadChildren: () =>
          import('./features/sap-qm/sap-qm.routes').then(m => m.SAP_QM_ROUTES),
      },

      // Legacy: top-level /Select-Language (not nested under a "settings" segment)
      {
        path: 'Select-Language',
        loadComponent: () =>
          import('./features/settings/select-language.component').then(m => m.SelectLanguageComponent),
      },

      // Feature 5: FAQ (Phase 6)
      {
        path: 'FAQ',
        loadChildren: () =>
          import('./features/faq/faq.routes').then(m => m.FAQ_ROUTES),
      },

      // Feature 5b: Supplier FAQ (Phase 6)
      {
        path: 'FAQ-Suppliers',
        loadChildren: () =>
          import('./features/faq/faq-suppliers.routes').then(m => m.FAQ_SUPPLIERS_ROUTES),
      },

      // Feature 6: Administration (Phase 6)
      {
        path: 'Administration',
        loadChildren: () =>
          import('./features/administration/administration.routes').then(m => m.ADMINISTRATION_ROUTES),
      },

      // Legacy: /Internal-Guest-Account-Activation (post-activation confirmation page)
      {
        path: 'Internal-Guest-Account-Activation',
        loadComponent: () =>
          import('./features/settings/internal-guest-account-activation.component').then(m => m.InternalGuestAccountActivationComponent),
      },
    ],
  },

  // Catch-all 404
  { path: '**', component: NotFoundComponent },
];
